import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * clients module ServiceStores.
 *
 * Declares the Feathers services the Clients-area ViewStores consume (migrated
 * off the removed `createRestApi`). ViewStores read `rootStore.<name>Store` and
 * call `.find/.get/.create/.patch/.remove` instead of a raw REST client.
 *
 * Only services UNIQUE to the Clients area are declared here. Several services
 * the Clients views also call are owned by sibling domain configs and are NOT
 * redeclared (the mapped `rootStore.<name>Store` is shared):
 *   - `clients`                              → system.stores.ts (global ClientStore)
 *   - `audit-log`                            → admin.stores.ts
 *   - `client-instances` / `instance-actions`→ infrastructure.stores.ts
 *   - `devops-connections` / `devops-links` / `devops-projects` /
 *     `devops-repos` / `devops-pipelines` / `devops-subscriptions`
 *                                            → devops.stores.ts
 *
 * idField: "id" for standard CRUD rows. The find-only rollup services
 * (`client-summary`, `client-health`, `client-blockers`) have NO `id` column —
 * each row is keyed by `clientId`, so that is their idField. Their ViewStores
 * read the `find()` RESPONSE rows (re-queried per load), not the entity cache.
 */
export const clientsStores: ServiceStoreConfig[] = [
  // Org-wide module vocabulary feeding the Modules panel's assignment picker.
  //
  // `serviceView` ON — the auto-generated raw grid at
  // /management/data/module-catalog, in the Management nav group (see
  // views/utils/ServiceViewRouteGenerator.ts). This table is reference data by
  // definition: `yarn catalog:sync` mirrors Module-Coordinator's catalog.json
  // into it, and every governed-identity column (governedRepo, category,
  // status, assignable, componentOf, catalogUpdated) is absent from the API's
  // data/patch schemas, so the sync is the only writer of the fields shown
  // here. Every consumer in the app uses the catalog as picker VOCABULARY —
  // nothing lists it raw, which is exactly what "did the last sync land?"
  // needs. Read-only in practice: no `actions` block ⇒ no inline edit and no
  // delete column, and `module-catalog` has no entry in the API's
  // api-services service-registry, so the generic Create dialog finds no
  // dataSchema and cannot POST. Explicit `columnHeaders` keep the JSON blob
  // columns (aliases, packages, legacyAliases) out of the grid.
  {
    name: "module-catalog",
    options: {
      idField: "id",
      enableStorage: false,
      serviceView: {
        enableServiceView: true,
        // Item 62 · These summed to 1460px. Measured on a phone, ~380px of
        // that (26%) was visible — Module Key, Kind and about 90px of Display
        // Name, with the third column cut mid-word on every row ("Ac", "A",
        // "B", "Ca"); at 820, 772px (53%), so five of ten columns were
        // unreachable. Booleans do not need 110px and dates do not need 140;
        // the identifying columns keep their room and the total comes down to
        // ~1000px, which is reachable with two swipes instead of five.
        columnHeaders: [
          { key: "moduleKey", title: "Module Key", dataType: "string", width: 170 },
          { key: "displayName", title: "Display Name", dataType: "string", width: 200 },
          { key: "adoProject", title: "ADO Project", dataType: "string", width: 150 },
          { key: "kind", title: "Kind", dataType: "string", width: 90 },
          { key: "status", title: "Status", dataType: "string", width: 100 },
          { key: "governedRepo", title: "Governed Repo", dataType: "string", width: 170 },
          { key: "category", title: "Category", dataType: "string", width: 110 },
          { key: "assignable", title: "Assignable", dataType: "boolean", width: 80 },
          { key: "isDeployable", title: "Deployable", dataType: "boolean", width: 80 },
          { key: "componentOf", title: "Component Of", dataType: "string", width: 130 },
          { key: "catalogUpdated", title: "Catalog Updated", dataType: "string", width: 120 },
        ],
        defaultSort: {
          sortBy: ["moduleKey"],
          sortDirection: { moduleKey: "ASC" },
        },
      },
    },
  },
  // Client child collections (standard CRUD; Client 360 tabs).
  { name: "client-modules", options: { idField: "id", enableStorage: false } },
  { name: "client-tools", options: { idField: "id", enableStorage: false } },
  { name: "client-contacts", options: { idField: "id", enableStorage: false } },
  // Each client's onboarding checklist (Blake's trackers, #3075 / #2663).
  {
    name: "client-onboarding-tasks",
    options: { idField: "id", enableStorage: false, enableOffline: false, disableInitialFind: true },
  },
  // Persisted DevOps event feed surfaced on the Activity timeline (find-only).
  { name: "devops-events", options: { idField: "id", enableStorage: false } },
  // Find-only rollups — no `id` column, one row per client (keyed by clientId).
  { name: "client-summary", options: { idField: "clientId", enableStorage: false } },
  { name: "client-health", options: { idField: "clientId", enableStorage: false } },
  // Append-only health telemetry (client_health_history): one row per observed
  // verdict, written internally by the client-health read. Unlike the rollups
  // above this IS a real table with an integer `id`, and it is staff-READ only
  // (find/get) — the Client 360 overview sparkline is its only consumer.
  {
    name: "client-health-history",
    options: { idField: "id", enableStorage: false },
  },
  // Find-only risk rollup (Client Risk view) — one row per client.
  { name: "client-blockers", options: { idField: "clientId", enableStorage: false } },
  // Clients' answers to our client inquiries (Clients → Client Inquiries, and
  // the Client 360 "Inquiries" tab). READ-ONLY: the API registers find/get
  // only, behind denyApiKeyPrincipal + requireStaffRead, and publishes no
  // events (`events: []`), so both views refresh on demand.
  //   - no initial find: nothing should pull 50 client decisions into every
  //     staff session on connect; the two views load their own pages;
  //   - merge mode + `findConcurrency: "parallel"`: the view stores keep only
  //     the ORDERED IDS of their page and resolve rows from this cache, so
  //     every response must land (latest-wins would discard the page one view
  //     asked for when the other view's find was issued after it). Rows are
  //     append-only, so merging never serves a stale row;
  //   - `throwOnError`: a failed find must reject, so "could not load" is never
  //     shown as "no answers yet";
  //   - no storage / no offline copy of client decisions.
  {
    name: "client-inquiry-answers",
    options: {
      idField: "id",
      enableStorage: false,
      enableOffline: false,
      disableInitialFind: true,
      methods: ["find", "get"],
      findConcurrency: "parallel",
      throwOnError: true,
    },
  },
  // The clients that HAVE answers (archived included) — the Client Inquiries
  // filter's options, so a client missing from the live `clients` list (archived,
  // or past its 200 cap) is still selectable. Find-only, one row per client
  // keyed by `clientId`, same staff-read gate as the answers. `replaceOnFind`:
  // each find is the whole set.
  {
    name: "client-inquiry-answer-clients",
    options: {
      idField: "clientId",
      enableStorage: false,
      enableOffline: false,
      disableInitialFind: true,
      methods: ["find"],
      replaceOnFind: true,
      throwOnError: true,
    },
  },
  // The client-inquiry RECORD — the question before it reaches the client
  // (Clients → Inquiry Queue; plan/2026-10-01_FROM-GANTRY_client-inquiries-contract.md).
  // READS only through this store: the view keeps the page's ordered ids and
  // resolves rows here. Actions (answer / release / withdraw) go through the
  // raw Feathers client — the API answers them with an envelope, not an
  // entity — and the view re-reads the record with `get` afterwards.
  //   - no initial find, no storage, no offline copy;
  //   - merge mode + `findConcurrency: "parallel"`, as client-inquiry-answers:
  //     a record changes stage, so a `get` after an action refreshes its row;
  //   - `throwOnError`: "could not load" is never shown as "no questions".
  {
    name: "client-inquiries",
    options: {
      idField: "id",
      enableStorage: false,
      enableOffline: false,
      disableInitialFind: true,
      methods: ["find", "get"],
      findConcurrency: "parallel",
      throwOnError: true,
    },
  },
  // A client question's PDFs (#4211), listed in the Inquiry Queue's dialog. Find
  // only, by `inquiryId`; rows carry their question's id and are filtered by it.
  // Making one goes through the RAW Feathers client (InquiryDeliveryStore).
  //   - no initial find, no storage, no offline copy;
  //   - `throwOnError`: "could not list" must never read as "no PDF yet".
  {
    name: "client-inquiry-pdfs",
    options: {
      idField: "documentId",
      enableStorage: false,
      enableOffline: false,
      disableInitialFind: true,
      methods: ["find"],
      findConcurrency: "parallel",
      throwOnError: true,
    },
  },
  // A client question's email draft (#17), keyed by the QUESTION's id: get only.
  // The send goes through the RAW Feathers client (InquiryDeliveryStore), whose
  // refusals reject. No storage and no offline copy: a draft names a client's
  // contacts and must not outlive the session on the device.
  {
    name: "client-inquiry-emails",
    options: {
      idField: "id",
      enableStorage: false,
      enableOffline: false,
      disableInitialFind: true,
      methods: ["get"],
      throwOnError: true,
    },
  },
  // Client 360's Overview in one read (#16 of 2026-09-25, #4039): the client,
  // its group, whether this person may change it (the client write gate), and
  // one section per thing staff act on, rolled up across a parent's companies.
  // Read by `get(clientId)` and keyed by `clientId` (ClientOverviewStore reads
  // the entity from here). Per person — the API reads every section as the
  // caller — so no storage, no offline copy, no initial find; `throwOnError`
  // so "could not load" is never shown as empty cards.
  {
    name: "client-overview",
    options: {
      idField: "clientId",
      enableStorage: false,
      enableOffline: false,
      disableInitialFind: true,
      methods: ["get"],
      throwOnError: true,
    },
  },
  // Client comms log (Client 360 "Comms" tab). Previously parked in
  // project-management.stores.ts; the `communications` service is client-scoped
  // and survived the removal of the project-management module.
  { name: "communications", options: { idField: "id", enableStorage: false } },
  // Client attachments (Client 360 "Documents"). Used via find/remove; uploads
  // go through a raw-body fetch endpoint directly. String `id`.
  { name: "documents", options: { idField: "id", enableStorage: false } },
];
